import { NUser } from "@nostrify/react/login";
import { NostrMetadata } from "@nostrify/nostrify";
import { join } from "path-browserify";
import OpenAI from "openai";
import { nip19 } from "nostr-tools";
import { JSRuntimeFS } from "./JSRuntime";
import type { Skill } from "@/contexts/SkillsContext";
import type { ProjectTemplate } from "@/contexts/AISettingsContext";
import { DEFAULT_SYSTEM_PROMPT_TEMPLATE } from "./defaultSystemPrompt";
import { DEFAULT_SYSTEM_PROMPT_TEMPLATE_VANILLA } from "./defaultSystemPromptVanilla";
import { renderTemplate } from "./templateRenderer";
import { detectProjectType, type ProjectType } from "./projectType";

export type ChatMode = 'ask' | 'act';

export interface MakeSystemPromptOpts {
  tools: OpenAI.Chat.Completions.ChatCompletionTool[];
  mode: "init" | "agent";
  fs: JSRuntimeFS;
  cwd: string;
  user?: NUser;
  metadata?: NostrMetadata;
  skills?: Skill[];
  chatMode?: ChatMode;
  /** Custom system prompt template for Nostr projects (Nunjucks format) */
  systemPromptTemplate?: string;
  /** Custom system prompt template for vanilla/Just UI projects (Nunjucks format) */
  systemPromptTemplateVanilla?: string;
  /** App configuration */
  config?: {
    corsProxy?: string;
    autoTranslate?: boolean;
    autoTranslateLanguages?: string[];
  };
  /** Project templates available for creating new projects */
  projectTemplates?: ProjectTemplate[];
}

export interface SystemPromptContext {
  mode: "init" | "agent";
  chatMode: ChatMode;
  date: string;
  cwd: string;
  location: {
    href: string;
    origin: string;
  };
  repositoryUrl: string | null;
  badgeUrl: string;
  editUrl: string;
  /** Detected project type based on dependencies */
  projectType: ProjectType;
  /** Whether the project has Nostr dependencies */
  isNostrProject: boolean;
  user: {
    pubkey: string;
    npub: string;
    name?: string;
    about?: string;
    website?: string;
    picture?: string;
    banner?: string;
    nip05?: string;
    lud16?: string;
  } | null;
  tools: OpenAI.Chat.Completions.ChatCompletionTool[];
  skills: Skill[];
  config: {
    corsProxy?: string;
    autoTranslate?: boolean;
    autoTranslateLanguages?: string[];
  };
  /** Project templates available for creating new projects */
  projectTemplates: ProjectTemplate[];
  README: string | null;
  AGENTS: string | null;
}

export async function makeSystemPrompt(opts: MakeSystemPromptOpts): Promise<string> {
  const { tools, mode, fs, cwd, user, metadata, skills = [], chatMode = 'act', systemPromptTemplate, systemPromptTemplateVanilla, config = {} } = opts;

  // Handle Ask mode separately (not templated)
  if (chatMode === 'ask') {
    return `You are Shakespeare, an expert software extraordinaire. You are currently in **Ask mode**, which means you should ONLY have a conversation with the user.

**IMPORTANT RESTRICTIONS IN ASK MODE:**
- Do NOT write, edit, or modify any code or files
- Do NOT use any tools that modify the filesystem (text_editor_write, text_editor_str_replace, npm_add_package, npm_remove_package, git_commit, build_project, deploy_project, shell commands that write files)
- You CAN use read-only tools to understand the project (text_editor_view, shell commands that only read)
- You CAN discuss code, explain concepts, answer questions, and provide suggestions
- If the user asks you to make changes, politely explain that you're in Ask mode and they need to switch to Act mode for you to make changes

Focus on understanding, explaining, and discussing. Help the user think through their requirements before they switch to Act mode.`;
  }

  // Build context for template
  const context = await buildTemplateContext(opts);

  // Select the appropriate template based on project type
  // For vanilla/Just UI projects, use the vanilla template (custom or default)
  // For Nostr projects or unknown, use the standard template
  let template: string;
  if (context.projectType === 'vanilla') {
    template = systemPromptTemplateVanilla?.trim() || DEFAULT_SYSTEM_PROMPT_TEMPLATE_VANILLA;
  } else if (systemPromptTemplate?.trim()) {
    template = systemPromptTemplate.trim();
  } else {
    template = DEFAULT_SYSTEM_PROMPT_TEMPLATE;
  }

  try {
    return renderTemplate(template, context as Record<string, unknown>);
  } catch (error) {
    console.error('Failed to render system prompt template:', error);
    // Fallback to default template on error
    return renderTemplate(DEFAULT_SYSTEM_PROMPT_TEMPLATE, context as Record<string, unknown>);
  }
}

async function buildTemplateContext(opts: MakeSystemPromptOpts): Promise<SystemPromptContext> {
  const { tools, mode, fs, cwd, user, metadata, skills = [], chatMode = 'act', config = {}, projectTemplates = [] } = opts;

  // Current date
  const date = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Location info
  const location = {
    href: typeof window !== 'undefined' ? window.location.href : 'https://shakespeare.diy',
    origin: typeof window !== 'undefined' ? window.location.origin : 'https://shakespeare.diy',
  };

  // Repository URL from git config
  const repositoryUrl = await getRepositoryUrl(fs, cwd);

  // Badge and edit URLs
  const badgeUrl = `${location.origin}/badge.svg`;
  const editUrl = repositoryUrl
    ? `${location.origin}/clone?url=${encodeURIComponent(repositoryUrl)}`
    : '';

  // User context
  let userContext: SystemPromptContext['user'] = null;
  if (user) {
    userContext = {
      pubkey: user.pubkey,
      npub: nip19.npubEncode(user.pubkey),
      name: metadata?.name,
      about: metadata?.about?.replace(/[\r\n]+/g, ' '),
      website: metadata?.website,
      picture: metadata?.picture,
      banner: metadata?.banner,
      nip05: metadata?.nip05,
      lud16: metadata?.lud16,
    };
  }

  // Load README.md
  let README: string | null = null;
  try {
    const readmePath = join(cwd, "README.md");
    README = await fs.readFile(readmePath, "utf8");
  } catch {
    // README.md not found
  }

  // Load AGENTS.md or other context files
  let AGENTS: string | null = null;
  try {
    const { text } = await getAgentContext(fs, cwd);
    AGENTS = text;
  } catch {
    // No context file found
  }

  // Detect project type based on dependencies
  const projectType = await detectProjectType(fs, cwd);
  const isNostrProject = projectType === 'nostr';

  return {
    mode,
    chatMode,
    date,
    cwd,
    location,
    repositoryUrl,
    badgeUrl,
    editUrl,
    projectType,
    isNostrProject,
    user: userContext,
    tools,
    skills,
    config,
    projectTemplates,
    README,
    AGENTS,
  };
}

async function getRepositoryUrl(fs: JSRuntimeFS, cwd: string): Promise<string | null> {
  try {
    const configPath = join(cwd, ".git/config");
    const configText = await fs.readFile(configPath, "utf8");

    // Parse git config to find remote origin URL
    const match = configText.match(/\[remote "origin"\][^\[]*url\s*=\s*(.+)/m);
    if (match) {
      return match[1].trim();
    }
  } catch {
    // No git config or couldn't read it
  }
  return null;
}

async function getAgentContext(
  fs: JSRuntimeFS,
  cwd: string,
): Promise<{ filename: string; text: string }> {
  const contextFiles = [
    "AGENTS.md",
    "CONTEXT.md",
    "CLAUDE.md",
    "codex.md",
    ".goosehints",
    ".cursorrules",
    ".github/copilot-instructions.md",
  ];

  for (const filename of contextFiles) {
    const filePath = join(cwd, filename);
    try {
      const text = await fs.readFile(filePath, "utf8");
      if (text) {
        return { filename, text };
      }
    } catch {
      // continue
    }
  }

  throw new Error(
    `No context file found in ${cwd}. Please create one of the following files: ${
      contextFiles.join(", ")
    }`,
  );
}

/** Export the default templates for use in settings UI */
export { DEFAULT_SYSTEM_PROMPT_TEMPLATE, DEFAULT_SYSTEM_PROMPT_TEMPLATE_VANILLA };
